import { useState } from 'react'
import { HardDrive, RefreshCw, Play, CheckCircle2, AlertTriangle } from 'lucide-react'
import { getAdminBackups, adminRunBackup } from '../../services/api'
import {
  C, Card, Row, Btn, BigStat, Loading, ErrorLine, Empty,
  useAsync, fmtDateTime, fmtTz, fmtSize, Pill, divider,
} from './adminUI'

// Раздел «Бэкапы» (задача из task.txt: «делает ли система какие-либо бекапы»).
// Бэкап = копия SQLite-БД (db + -wal + -shm, после пассивного WAL-checkpoint)
// и папок users_DATA / users_BOTS без logs/. Сервер при этом НЕ остановлен.
// Расписание: раз в BACKUP_INTERVAL_HOURS часов (по умолчанию 24, 0 — выкл).
// Старые бэкапы удаляются автоматически через BACKUP_KEEP_DAYS дней.

export default function BackupsSection() {
  const { data, loading, error, reload } = useAsync(() => getAdminBackups(), [])
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')

  const run = async () => {
    setBusy(true); setNote('')
    try {
      const before = data?.lastRun?.finishedAt || null
      const r = await adminRunBackup()
      // Бэкап выполняется отдельным процессом (backup_scheduler.js --once):
      // API отвечает 202 сразу, результат появляется в lastRun после финиша.
      if (!r?.started) {
        setNote(`Бэкап готов: ${r?.name || '—'} (${fmtSize(r?.size)})`)
        reload()
        return
      }
      setNote('Бэкап запущен в фоне. Жду завершения...')
      for (let i = 1; i <= 20; i++) {
        await new Promise(res => setTimeout(res, 2000))
        const d = await getAdminBackups()
        if (d?.lastRun?.finishedAt && d.lastRun.finishedAt !== before) {
          setNote(`Бэкап готов: ${d.lastRun.name || '—'} (${fmtSize(d.lastRun.size)})`)
          reload()
          return
        }
      }
      setNote('Бэкап всё ещё выполняется — результат появится при следующем обновлении.')
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy(false) }
  }

  const list = data?.backups || []
  const last = data?.lastRun
  const totalSize = list.reduce((s, b) => s + (Number(b.size) || 0), 0)

  return (
    <div>
      <ErrorLine onRetry={reload}>{error}</ErrorLine>
      {note && (
        <div style={{ fontSize: 12, color: note.startsWith('Ошибка') ? C.danger : C.accent, marginBottom: 10 }}>
          {note}
        </div>
      )}

      {loading && !data ? <Loading /> : (
        <>
          <Card
            title="Расписание"
            icon={HardDrive}
            right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={reload}>Обновить</Btn>}
          >
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <BigStat
                label="Автобэкап"
                value={data?.intervalHours ? `${data.intervalHours}ч` : 'выкл'}
                color={data?.intervalHours ? C.accent : C.danger}
              />
              <BigStat label="Копий" value={list.length} color="#fff" />
              <BigStat label="Занято" value={fmtSize(totalSize)} color={C.indigo} />
            </div>
            <Row label="Хранить дней" value={data?.keepDays ?? '—'} />
            <Row label="Папка" value={data?.dir || '—'} mono />
            {!data?.intervalHours && (
              <div style={{
                marginTop: 10, display: 'flex', gap: 8, alignItems: 'flex-start',
                background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
                borderRadius: 12, padding: '9px 11px',
              }}>
                <AlertTriangle size={14} color={C.danger} style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 11.5, color: C.danger, lineHeight: 1.45 }}>
                  Автобэкап отключён (BACKUP_INTERVAL_HOURS=0). Копии делаются
                  только вручную кнопкой ниже.
                </div>
              </div>
            )}
            <div style={divider} />
            <Btn icon={Play} disabled={busy} onClick={run}>{busy ? 'Копирую…' : 'Сделать бэкап сейчас'}</Btn>
            <div style={{ fontSize: 10.5, color: C.dim, marginTop: 10, lineHeight: 1.45 }}>
              Копируется БД (вместе с WAL) и папки users_DATA / users_BOTS без логов.
              Сервер и боты продолжают работать.
            </div>
          </Card>

          {last && (
            <Card
              title="Последний запуск"
              icon={last.ok ? CheckCircle2 : AlertTriangle}
              accent={last.ok ? C.accent : C.danger}
            >
              <Row
                label="Результат"
                value={last.ok ? 'успешно' : 'ошибка'}
                valueColor={last.ok ? C.accent : C.danger}
              />
              <Row label="Причина запуска" value={last.reason === 'manual' ? 'вручную' : 'по расписанию'} />
              <Row label={`Время (${fmtTz()})`} value={fmtDateTime(last.startedAt)} />
              {last.ok ? (
                <>
                  <Row label="Папка" value={last.name || '—'} mono />
                  <Row label="Файлов БД" value={last.dbFiles ?? '—'} />
                  <Row label="Папок юзеров" value={(last.folders || []).join(', ') || '—'} />
                  <Row label="Размер" value={fmtSize(last.size)} />
                  {!!(last.pruned || []).length && (
                    <Row label="Удалено старых" value={last.pruned.length} valueSub={last.pruned.join(', ')} />
                  )}
                </>
              ) : (
                <Row label="Текст ошибки" value={last.error || '—'} valueColor={C.danger} />
              )}
            </Card>
          )}

          <Card title={`Копии (${list.length})`} icon={HardDrive} accent={C.muted}>
            {!list.length ? <Empty>Бэкапов ещё нет</Empty> : list.map((b, i) => (
              <div key={b.name} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                borderTop: i ? `1px solid ${C.line}` : 'none', padding: '9px 0',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: '#fff', fontFamily: 'monospace' }}>{b.name}</div>
                  <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>{fmtDateTime(b.createdAt)}</div>
                </div>
                <Pill color={i === 0 ? C.accent : C.muted}>{fmtSize(b.size)}</Pill>
              </div>
            ))}
            <div style={divider} />
            <div style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.45 }}>
              Восстановление: остановить сервер и ботов, скопировать файлы из папки
              бэкапа на место (БД + users_DATA/users_BOTS), запустить снова.
            </div>
          </Card>
        </>
      )}
    </div>
  )
}
